import { FC, ReactNode } from 'react';
import { createUseStyles } from 'react-jss';
import { IFamilyParticipation, describeParticipation } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK } from './agencyChartTheme';
import { InsightsInfoTip } from './InsightsInfoTip';
import { BeaSays } from './BeaSays';
import { IInsightsClientList } from './InsightsClientListModal';

const useStyles = createUseStyles({
    section: {
        marginTop: 18,
        paddingTop: 14,
        borderTop: `1px solid ${BRAND.tealEdge}`
    },

    head: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        marginBottom: 12,
        fontSize: 13.5,
        fontWeight: 600,
        color: BRAND.deepTeal
    },

    heading: {
        flex: 1,
        minWidth: 0
    },

    figures: {
        display: 'grid',
        // Four figures to a row in the reading column, so none falls alone under the other three.
        gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
        gap: 12
    },

    figure: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 2,
        width: '100%',
        padding: '12px 14px',
        borderRadius: 11,
        border: `1px solid ${BRAND.tealEdge}`,
        background: BRAND.tealWash,
        font: 'inherit',
        color: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',
        transition: 'border-color 140ms ease',

        '&:hover, &:focus-visible': {
            borderColor: BRAND.deepTeal
        },

        '@media print': {
            breakInside: 'avoid'
        }
    },

    figureValue: {
        fontSize: 26,
        fontWeight: 600,
        lineHeight: 1.1,
        color: BRAND.deepTeal,
        fontVariantNumeric: 'tabular-nums'
    },

    figureUnit: {
        fontSize: 15,
        fontWeight: 500
    },

    figureLabel: {
        fontSize: 12.5,
        lineHeight: 1.4,
        color: CHART_INK.label
    },

    // An invitation to press, which paper cannot take up.
    figureMore: {
        marginTop: 3,
        fontSize: 11.5,
        fontWeight: 500,
        color: BRAND.deepTeal,

        '@media print': {
            display: 'none'
        }
    }
});

const PARTICIPATION_EXPLAINER =
    'Calculated from the record over the last 7 days. A client is participating when their family has '
    + 'put at least one observation on file in that week.';

/** One figure: the number, what it counts, and the way into the clients behind it. */
const ParticipationFigure: FC<{
    dataId: string;
    label: string;
    onClick: () => void;
    /** The figure itself. */
    children: ReactNode;
}> = ({ dataId, label, onClick, children }) => {
    const classes = useStyles();

    return (
        <button type="button" className={classes.figure} data-id={dataId} onClick={onClick}>
            <span className={classes.figureValue}>{children}</span>
            <span className={classes.figureLabel}>{label}</span>
            <span className={classes.figureMore}>See who &rsaquo;</span>
        </button>
    );
};

/**
 * How much of the book is contributing, as a section of the Outcomes Report: a seven-day count,
 * real at any caseload age. On a young book a low figure is the onboarding gap.
 */
export const FamilyParticipationSection: FC<{
    participation: IFamilyParticipation;
    unreadableCount: number;
    /** Opens the clients behind a figure. */
    onOpenList: (list: IInsightsClientList) => void;
}> = ({ participation, unreadableCount, onOpenList }) => {
    const classes = useStyles();

    const openParticipating = () => onOpenList({
        title: 'Families Contributing This Week',
        blurb: 'Clients whose family put at least one observation on the record in the last seven days.',
        clients: participation.participating
    });

    // The observations figure counts observations, not clients, so it opens a list that says how
    // many each family filed. Sending it to the same list as the client figures put a count of
    // eight above a list of seven people, with nothing explaining the difference.
    const openSignal = () => onOpenList({
        title: 'Who Filed Observations This Week',
        blurb: 'How many observations each family put on the record in the last seven days.',
        clients: participation.participating
    });

    const openQuiet = () => onOpenList({
        title: 'Clients With No Family Signal',
        blurb: 'Nothing on file from these families in the last seven days. Usually an onboarding gap rather than a quiet household.',
        clients: participation.quiet
    });

    return (
        <div className={classes.section} data-id="family-participation-section">
            <div className={classes.head}>
                <span className={classes.heading}>Family Participation</span>
                <InsightsInfoTip label="How participation is counted" summary={PARTICIPATION_EXPLAINER} />
            </div>

            <div className={classes.figures} data-id="family-participation">
                <ParticipationFigure dataId="participation-rate" onClick={openParticipating} label="of clients had a family reporting this week">
                    {participation.participationPercent}%
                </ParticipationFigure>

                <ParticipationFigure dataId="participation-count" onClick={openParticipating} label="clients with family signal on file">
                    {participation.participatingCount}
                    <span className={classes.figureUnit}> / {participation.clientCount}</span>
                </ParticipationFigure>

                <ParticipationFigure dataId="participation-signal" onClick={openSignal} label="observations recorded this week">
                    {participation.signalCount}
                </ParticipationFigure>

                <ParticipationFigure
                    dataId="participation-quiet"
                    onClick={openQuiet}
                    label={`${participation.quietCount === 1 ? 'client to onboard' : 'clients to onboard'}, no family signal this week`}
                >
                    {participation.quietCount}
                </ParticipationFigure>
            </div>

            <BeaSays panel="participation">
                {describeParticipation({ participation, unreadableCount })}
            </BeaSays>
        </div>
    );
};
